import React from 'react';

import { MutationResult, QueryResult } from '@apollo/client';

import { TaxImpactPreviewError } from './TaxImpactPreviewError';
import { RequiredTaxImpactPreview, TaxImpactPreviewLoaded } from './TaxImpactPreviewLoaded';
import { TaxImpactPreviewLoading } from './TaxImpactPreviewLoading';

import { CreateFinancialSecurityOrderMutation } from '../graphql/mutation.createFinancialSecurityOrder.generated';
import { CreateFinancialSecuritySellOrderTaxImpactPreviewMutation } from '../graphql/mutation.createFinancialSecuritySellOrderTaxImpactPreview.generated';
import { PurchaseOrderAccountHoldingsQuery } from '../graphql/query.purchaseOrderaccountHoldings.generated';

type TaxImpactPreviewData =
  CreateFinancialSecuritySellOrderTaxImpactPreviewMutation['createFinancialSecuritySellOrderTaxImpactPreview']['result'];

const isRequiredTaxImpactPreview = (
  data: TaxImpactPreviewData | null | undefined
): data is RequiredTaxImpactPreview => {
  return !!data;
};

interface Props {
  previewResult: Pick<
    MutationResult<CreateFinancialSecuritySellOrderTaxImpactPreviewMutation>,
    'loading' | 'data' | 'error'
  >;
  accountHoldingsResult: Pick<QueryResult<PurchaseOrderAccountHoldingsQuery>, 'loading' | 'data' | 'error'>;
  submitResult: Pick<MutationResult<CreateFinancialSecurityOrderMutation>, 'loading' | 'data' | 'error'>;
  onSubmit: () => void;
}

export const TaxImpactPreviewStateSwitch: React.FC<Props> = ({
  previewResult,
  accountHoldingsResult,
  submitResult,
  onSubmit,
}) => {
  if (previewResult.loading && !previewResult.data) {
    return <TaxImpactPreviewLoading />;
  }

  const previewData = previewResult.data?.createFinancialSecuritySellOrderTaxImpactPreview?.result;
  const previewDisplayableErrors =
    previewResult.data?.createFinancialSecuritySellOrderTaxImpactPreview?.displayableErrors;
  const submitOrderDisplayableErrors = submitResult.data?.createFinancialSecurityOrderRequest?.displayableErrors;
  const hasActiveQueries = accountHoldingsResult.loading || submitResult.loading;

  if (previewResult.error || !isRequiredTaxImpactPreview(previewData)) {
    return (
      <TaxImpactPreviewError
        onSubmit={onSubmit}
        hasActiveQueries={hasActiveQueries}
        submitOrderDisplayableErrors={submitOrderDisplayableErrors}
        submitOrderMutationError={submitResult.error}
        previewDisplayableErrors={previewDisplayableErrors}
        previewMutationError={previewResult.error}
      />
    );
  }

  return (
    <TaxImpactPreviewLoaded
      onSubmit={onSubmit}
      hasActiveQueries={hasActiveQueries}
      submitOrderDisplayableErrors={submitOrderDisplayableErrors}
      submitOrderMutationError={submitResult.error}
      previewData={previewData}
    />
  );
};
